import { useEffect, useState } from 'react'
import type { Dispatch, SetStateAction } from 'react'

import { useSetState } from '@domain-group/react-hooks'

import { getQueryParamsFromHash, modifyGroupedIdsSort, parsePermittedQueryParams } from '@domain-group/javascript-utils'

import useCampaignQuote from '@hooks/useCampaignQuote'
import useCampaignQuoteItems from '@hooks/useCampaignQuoteItems'
import type { GenerateUrlsOptions as QuoteGenerateUrlsOptions } from '@hooks/useCampaignQuote'

import type {
  CampaignModel, CampaignQuoteItemModel,
} from '@models/types'

export const defaultRequestOptions = {
  artwork: {
    include_artwork_data_changes: true,
    include_artwork_pages: true,
  },
  artworkPage: {
    include_artwork_page_attachments: true,
    include_artwork_page_preview: true,
    include_artwork_page_warning_count: true,
  },
  attachment: {
    include_attachment_file: true,
  },
  campaignQuote: {
    include_artwork_artwork_reviews: true,
    include_artwork_data_changes: true,
    include_artwork_page_attachments: true,
    include_artwork_page_preview: true,
    include_artwork_pages: true,
    include_artwork_page_warning_count: true,
    include_campaign_quote_agency_payment_method: true,
    include_campaign_quote_document_signers: true,
    include_campaign_quote_item_artwork: true,
    include_campaign_quote_items: true,
    include_campaign_quote_links: true,
    include_campaign_quote_sections: true,
    include_campaign_quote_status: true,
    include_campaign_quote_template: true,
  },
  campaignQuoteItem: {
    include_campaign_quote_item_artwork: true,
    include_campaign_quote_item_deep_link: true,
    include_campaign_quote_item_publication: true,
    include_campaign_quote_item_publication_edition: true,
    include_campaign_quote_item_publication_package: true,
    include_campaign_quote_item_quote_total: true,
    include_campaign_quote_item_service: true,
    include_campaign_quote_item_service_product: true,
  },
}

const defaultState = {
  manuallySorted: false,
}

type SetState = (state: Partial<typeof defaultState>) => void

type SetupSortedSectionItemIds = {
  filteredCampaignQuoteItems: CampaignQuoteItemModel[],
  setSortedSectionItemIds: Dispatch<SetStateAction<typeof defaultState>>,
}

const setupSortedSectionItemIds = (params: SetupSortedSectionItemIds) => {
  const { filteredCampaignQuoteItems, setSortedSectionItemIds } = params

  const grouped = filteredCampaignQuoteItems.reduce((acc, quoteItem) => {
    const { campaign_quote_section_id } = quoteItem

    const key = `Section-${campaign_quote_section_id}`
    // @ts-expect-error TS7053
    if (!acc[key]){
      // @ts-expect-error TS7053
      acc[key] = []
    }

    // @ts-expect-error TS7053
    acc[key].push(quoteItem.id)
    return acc
  }, {})

  // @ts-expect-error TS2345
  setSortedSectionItemIds(grouped)
}

type DragDropPayload = {
  destinationDroppableId: string,
  sourceDroppableId: string,
}

type ModifySectionItemSort = {
  dragDropPayload: DragDropPayload,
  fromIndex: number,
  setSortedSectionItemIds: Dispatch<SetStateAction<{}>>,
  setState: SetState,
  toIndex: number,
}

const modifySectionItemSort = (params: ModifySectionItemSort) => {
  const {
    dragDropPayload,
    fromIndex,
    setSortedSectionItemIds,
    setState,
    toIndex,
  } = params

  const { destinationDroppableId, sourceDroppableId } = dragDropPayload

  setSortedSectionItemIds((sortedSectionItemIds) => {
    const groupedIds = modifyGroupedIdsSort(
      fromIndex,
      toIndex,
      dragDropPayload,
      sortedSectionItemIds,
    )

    return {
      ...sortedSectionItemIds,
      [destinationDroppableId]: groupedIds[destinationDroppableId],
      [sourceDroppableId]: groupedIds[sourceDroppableId],
    }
  })

  setState({ manuallySorted: true })
}

type UseQuoteBuilderOptions = {
  campaign: CampaignModel,
  campaignQuoteId: number,
  generateUrlsOptions?: QuoteGenerateUrlsOptions,
}

function useQuoteBuilder(options: UseQuoteBuilderOptions) {
  const { campaign, campaignQuoteId, generateUrlsOptions } = options

  const queryParams: { cacheKey?: string } = parsePermittedQueryParams(getQueryParamsFromHash(), [
    'cacheKey',
  ])

  const [state, setState] = useSetState(defaultState)
  const { manuallySorted } = state

  const [sortedSectionItemIds, setSortedSectionItemIds] = useState({})

  const campaignQuotePayload = useCampaignQuote({ id: campaignQuoteId }, {
    generateUrlsOptions,
    performHttpRequests: !!campaignQuoteId && !!campaign.cache_key,
    requestOptions: {
      cache_key: queryParams.cacheKey,
      ...defaultRequestOptions.artwork,
      ...defaultRequestOptions.artworkPage,
      ...defaultRequestOptions.attachment,
      ...defaultRequestOptions.campaignQuote,
      ...defaultRequestOptions.campaignQuoteItem,
      // @ts-expect-error TS2561
      include_campaign_quote_item_quote_total: false,
    },
    shouldLoadReconciliationTotals: true,
  })

  const {
    callbacks: {
      updateCampaignQuoteSectionItemOrder,
    },
  } = campaignQuotePayload

  const campaignQuoteItemsPayload = useCampaignQuoteItems({
    filters: {
      campaign_quote_id: campaignQuoteId,
    },
    performHttpRequests: false,
  })

  const { filteredCampaignQuoteItems } = campaignQuoteItemsPayload

  // Handle item sorting in state
  useEffect(() => {
    setupSortedSectionItemIds({ filteredCampaignQuoteItems, setSortedSectionItemIds })
  }, [filteredCampaignQuoteItems.length])

  // Commit the sorted items
  useEffect(() => {
    if (manuallySorted){
      updateCampaignQuoteSectionItemOrder(sortedSectionItemIds, {
        ...defaultRequestOptions.campaignQuote,
        ...defaultRequestOptions.campaignQuoteItem,
        // @ts-expect-error TS2561
        include_campaign_quote_item_quote_total: false,
      })

      setState({ manuallySorted: false })
    }
  }, [JSON.stringify(sortedSectionItemIds), manuallySorted])

  return {
    callbacks: {
      modifySectionItemSort: (params: {
        dragDropPayload: DragDropPayload,
        fromIndex: number,
        toIndex: number,
      }) => modifySectionItemSort({
        setSortedSectionItemIds,
        setState,
        ...params,
      }),
    },
    campaignQuotePayload,
    sortedSectionItemIds,
  }
}

export default useQuoteBuilder
